<template>
  <div class="zone-field">
    <span class="zone-label">{{ label }}</span>
    <div class="zone-code-row">
      <div
        ref="code"
        class="zone-code"
      >{{ value }}</div>
      <button
        type="button"
        class="zbtn"
        :class="`zbtn-${variant}`"
        :data-testid="testid"
        @click="copy"
      >
        <i class="material-symbols-rounded">{{ copied ? 'check' : 'content_copy' }}</i>
        {{ copied ? 'Copied' : 'Copy' }}
      </button>
    </div>
  </div>
</template>

<script>
const COPIED_FOR = 1400;

export default {
  name: 'McpCopyRow',
  props: {
    label: { type: String, required: true },
    value: { type: String, required: true },
    testid: { type: String, default: null },
    variant: { type: String, default: 'primary' },
  },
  data() {
    return { copied: false };
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    async copy() {
      if (!await this.writeToClipboard()) {
        // Nothing was copied, so the button must not claim otherwise — select the value instead.
        this.selectValue();

        return;
      }

      this.copied = true;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.copied = false; }, COPIED_FOR);
    },
    // The Clipboard API is unavailable over plain HTTP and can be refused by permissions.
    async writeToClipboard() {
      try {
        await navigator.clipboard.writeText(this.value);

        return true;
      } catch (e) {
        return this.copyViaTextarea();
      }
    },
    copyViaTextarea() {
      const area = document.createElement('textarea');

      area.value = this.value;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();

      let copied = false;

      try {
        copied = document.execCommand('copy');
      } catch (e) {
        copied = false;
      }

      area.remove();

      return copied;
    },
    selectValue() {
      const code = this.$refs.code;

      if (!code || !window.getSelection) {return;}

      const range = document.createRange();
      const selection = window.getSelection();

      range.selectNodeContents(code);
      selection.removeAllRanges();
      selection.addRange(range);
    },
  },
};
</script>

<style scoped>
.zone-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.zone-label {
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.zone-code-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.zone-code {
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  align-items: center;
  height: 40px;
  padding: 10px 12px;
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: var(--rounded-md);
  color: var(--gray-700);
  font-family: Menlo, ui-monospace, SFMono-Regular, monospace;
  font-size: 12px;
  line-height: normal;
  overflow: auto;
  white-space: nowrap;
}

.zbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-height: 40px;
  padding: 10px 20px;
  border: 1px solid transparent;
  border-radius: var(--rounded-md);
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
}

.zbtn .material-symbols-rounded {
  font-size: 18px;
  line-height: 16px;
}

.zbtn-primary {
  background: var(--primary-500);
  color: #fff;
}

.zbtn-primary:hover {
  background: var(--primary-600);
}

.zbtn-outline {
  border-color: var(--primary-500);
  color: var(--primary-500);
  background: #fff;
}

.zbtn-outline:hover {
  background: var(--primary-50);
}

@media (max-width: 720px) {
  .zone-code-row {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
}
</style>
